import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Select,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Switch,
  Row,
  Col,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import IconShop from "../../static/icon/IconShop";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../../components/Dropdown/MerchantDropdown";
import LandlordDropdown from "../../components/Dropdown/LandlordDropdown";

const MerchantTable = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [merchants, setMerchants] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentMerchant, setCurrentMerchant] = useState(null);
  const [currentLandlord, setCurrentLandlord] = useState(null);

  const toggleExpandedKeys = (key) => {
    setExpandedMerchants((prev) => {
      const outArr = [...prev];
      if (outArr.includes(key)) {
        return outArr.filter((e) => e !== key);
      } else {
        outArr.push(key);
        return outArr;
      }
    });
  };

  useEffect(() => {
    async function getMerchants({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/merchant', window.location);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (typeof searchValue.internal === 'boolean') url.searchParams.append('internal', searchValue.internal);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, merchants } = await res.json();
        setTotal(total);
        setMerchants(merchants);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getMerchants(search);
  }, [search, accessToken, update]);

  const [expandedMerchants, setExpandedMerchants] = useState([]);

  const columns = [
    {
      title: merchants.every((merchant) => expandedMerchants.includes(merchant.id)) ? (
        <CaretUpOutlined
          style={{ fontSize: 16 }}
          className="cursor-pointer"
          onClick={() => setExpandedMerchants([])}
        />
      ) : (
        <CaretDownOutlined
          style={{ fontSize: 16 }}
          className="cursor-pointer"
          onClick={() => setExpandedMerchants(merchants.map(merchant => merchant.id))}
        />
      ),
      key: "expand",
      width: 50,
      render: (_, merchant) =>
        expandedMerchants.includes(merchant.id) ? (
          <CaretUpOutlined
            style={{ fontSize: 16 }}
            className="cursor-pointer"
            onClick={() => toggleExpandedKeys(merchant.id)}
          />
        ) : (
          <CaretDownOutlined
            style={{ fontSize: 16 }}
            className="cursor-pointer"
            onClick={() => toggleExpandedKeys(merchant.id)}
          />
        ),
    },
    {
      title: "Cluster ID",
      dataIndex: "id",
      key: "id",
      sorter: () => null,
    },
    {
      title: "Cluster Name",
      dataIndex: "nameEn",
      key: "nameEn",
      sorter: () => null,
    },
    {
      title: "Area type",
      dataIndex: "internal",
      key: "internal",
      width: 120,
      sorter: () => null,
      render: (internal) => internal ? 'Internal Used' : 'Public Used',
    },
    {
      title: "Inactive/Active",
      dataIndex: "active",
      key: "active",
      width: "10%",
      align: "center",
      sorter: () => null,
      render: (active, merchant) => (
        <Switch
          checked={active}
          onChange={(active) => {
            updateMerchantActive(active, merchant);
            setUpdate(update + 1);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      render: (_, merchant) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="Edit" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/merchant/${merchant.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
            />
          </Tooltip>

          {!merchant.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentMerchant(merchant)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  async function updateMerchantActive(active, merchant) {
    const url = new URL('/api/merchant/' + merchant.id, window.location);

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'X-ACCESS-TOKEN': accessToken,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ active }),
    });
    if (res.ok) {
      notification.success({
        message: "Success",
        description: "Successfully saved data.",
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }
  };

  async function updateLandlordActive(active, landlord) {
    const url = new URL('/api/landlord/' + landlord.id, window.location);

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'X-ACCESS-TOKEN': accessToken,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ active }),
    });
    if (res.ok) {
      notification.success({
        message: "Success",
        description: "Successfully saved data.",
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }
  };

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/merchant', window.location);
    url.searchParams.append('mode', 'xlsx');

    if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
    if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
    if (typeof searchValue.internal === 'boolean') url.searchParams.append('internal', searchValue.internal);
    if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `Merchant_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched merchant found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          className="p-0 w-full"
          title={
            <span className="flex items-center mt-2">
              <IconShop /> &nbsp;&nbsp;Cluster
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() =>
            history.push({
              pathname: "/merchant/new",
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            })
          }
        >
          New Cluster
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Cluster Name"
                  name="merchantIds"
                  colon={false}
                  initialValue={[]}
                >
                  <MerchantDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Area Type"
                  name="internal"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Area Type' },
                      { value: true, label: 'Internal Used' },
                      { value: false, label: 'Public Used' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={20}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(merchant) => merchant.id}
          loading={loading}
          columns={columns}
          dataSource={merchants}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
          expandable={{
            expandIconColumnIndex: -1,
            onExpandedRowsChange: (e) => setExpandedMerchants(e),
            expandedRowKeys: expandedMerchants,
            expandedRowRender: (merchant) => (
              <Row className="expand-custom-header" gutter={[16, 16]}>
                <Col span={4} className="font-medium">
                  Site Name ({merchant.landlords.length})
                </Col>

                <Col span={14} className="font-medium">
                  {merchant.landlords.map((landlord) => (
                    <>
                      <Row className="mb-2">
                        <Col span={16}>{landlord.nameEn}</Col>
                        <Col span={4}>
                          <Switch
                            checked={landlord.active}
                            onChange={(value) => {
                              updateLandlordActive(value, landlord);
                              setUpdate(update + 1);
                            }}
                          />
                        </Col>
                        <Col span={4}>
                          {!landlord.active && (
                            <DeleteTwoTone
                              onClick={() => setCurrentLandlord(landlord)}
                              twoToneColor="red"
                            />
                          )}
                        </Col>
                      </Row>
                    </>
                  ))}
                </Col>
              </Row>
            ),
          }}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentMerchant}
        name="Merchant"
        type="merchant"
        editRecord={currentMerchant}
        onCancel={() => setCurrentMerchant(null)}
        onOk={() => {
          setCurrentMerchant(null);
          setUpdate(update + 1);
        }}
      />
      <DeleteAction
        openModalActionDelete={!!currentLandlord}
        name="Landlord"
        type="landlord"
        editRecord={currentLandlord}
        onCancel={() => setCurrentLandlord(null)}
        onOk={() => {
          setCurrentLandlord(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default MerchantTable;
